import React, { useEffect, useState } from "react";
import { uniqueUUID } from "../../utils";
import { PersonFormStateError } from "../PersonFormErrorState";

import { useLoadScript } from "@react-google-maps/api";
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";

import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
  ComboboxPopover,
} from "@reach/combobox";
import "@reach/combobox/styles.css";
import Input from "../../shared-components/Input";

export interface AddressesDefinitions {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  key: string;
}

type Props = {
  onChange?: (aliases: AddressesDefinitions) => void;
  error?: PersonFormStateError;
  prefill?: AddressesDefinitions;
};

function buildEmptyDefinition(): AddressesDefinitions {
  return {
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    key: uniqueUUID(),
  };
}

const PersonFormAddressField = ({ onChange, error, prefill }: Props) => {
  const [libraries] = useState<["places"]>(["places"]);
  useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GTM_KEY || "",
    libraries,
  });

  const {
    ready,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({ debounce: 300 });

  const [fieldsList, setFields] = useState<AddressesDefinitions>(
    buildEmptyDefinition()
  );
  const updateField = (
    field: "street" | "city" | "state" | "zip" | "country",
    value: string
  ) => {
    fieldsList[field] = value;
    handleChange(fieldsList);
  };

  const handleChange = (list: AddressesDefinitions) => {
    onChange?.(list);
    setFields({ ...list });
    clearSuggestions();
  };

  useEffect(() => {
    if (prefill) {
      handleChange(prefill);
      let addressComponents = [
        prefill.street,
        prefill.city,
        prefill.state,
        prefill.zip,
        prefill.country,
      ];
      let _addressString = addressComponents.filter(Boolean).join(", ");
      setValue(_addressString, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const handleSelect = async (address: string) => {
    setValue(address, false);
    const results = await getGeocode({ address });
    let street: string = "";
    let street_number: string = "";
    let route: string = "";

    let components = results[0].address_components;
    components.forEach((component) => {
      if (component.types.includes("street_number")) {
        street_number = component.long_name;
      } else if (component.types.includes("route")) {
        route = component.long_name;
      }
      if (component.types.includes("locality")) {
        updateField("city", component.long_name);
      } else if (component.types.includes("administrative_area_level_1")) {
        updateField("state", component.short_name);
      } else if (component.types.includes("postal_code")) {
        updateField("zip", component.short_name);
      } else if (component.types.includes("country")) {
        updateField("country", component.short_name);
      }

      if (street_number && route) {
        street = `${street_number} ${route}`;
      } else if (route) {
        street = route;
      } else if (street_number) {
        street = street_number;
      }
      updateField("street", street);
    });
    clearSuggestions();
  };
  return (
    <>
      <div key={fieldsList.key} className="flex flex-wrap">
        <Combobox onSelect={(e) => handleSelect(e)}>
          <label
            htmlFor={`street-name`}
            className={"block text-sm font-medium text-gray-600 mb-2"}
          >
            Street
          </label>
          <ComboboxInput
            onChange={(e) => {
              setValue(e.target.value);
              updateField("street", e.target.value);
            }}
            disabled={!ready}
            name={`street-name`}
            value={fieldsList.street}
            placeholder={""}
            autoComplete="off"
            style={{ width: "100%" }}
            className={
              "border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 w-96"
            }
          />
          <ComboboxPopover>
            <ComboboxList>
              {status === "OK" &&
                data.map((data) => {
                  return (
                    <ComboboxOption
                      key={data.place_id}
                      value={data.description}
                    />
                  );
                })}
            </ComboboxList>
          </ComboboxPopover>
        </Combobox>
        <div className="ml-4 mr-4 flex-1">
          <label
            htmlFor={`city`}
            className={"block text-sm font-medium text-gray-600 mb-2"}
          >
            City
          </label>
          <Input
            name={"city"}
            value={fieldsList.city}
            className="w-full"
            onChange={(event) => updateField("city", event.target.value)}
          ></Input>
        </div>
        <div className="mr-4  flex-1">
          <label
            htmlFor={`state`}
            className={"block text-sm font-medium text-gray-600 mb-2"}
          >
            State
          </label>
          <Input
            name={"state"}
            value={fieldsList.state}
            onChange={(event) => updateField("state", event.target.value)}
            className="w-full"
          ></Input>
        </div>

        <div className="mr-4  flex-1">
          <label
            htmlFor={`postal-code`}
            className={"block text-sm font-medium text-gray-600 mb-2"}
          >
            Zip Code
          </label>
          <Input
            name={"postal-code"}
            value={fieldsList.zip}
            className="w-full"
            onChange={(event) => updateField("zip", event.target.value)}
          ></Input>
        </div>
        <div className="mr-4  flex-1">
          <label
            htmlFor={`country`}
            className={"block text-sm font-medium text-gray-600 mb-2"}
          >
            Country
          </label>
          <Input
            name={"country"}
            value={fieldsList.country}
            onChange={(event) => updateField("country", event.target.value)}
            className="w-full"
          ></Input>
        </div>
      </div>
    </>
  );
};

export default PersonFormAddressField;
